import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  AdminAuthButton, AdminAuthField, AdminAuthShell,
} from "@/pages/admin/AdminForgotPassword";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those two passwords do not match.");
      return;
    }
    if (password.trim().length < 12) {
      setError("Choose a password of at least 12 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      nav("/admin/login", { replace: true, state: { from: "/admin/growth" } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token)
    return (
      <AdminAuthShell heading="Link not valid" footer="Access is restricted to Human Home Living staff.">
        <p className="text-[14.5px] text-[#454545] leading-relaxed" data-testid="admin-reset-no-token">
          This page needs the link from your reset email. Request a new one and open it from the
          email itself.
        </p>
        <Link
          to="/admin/forgot-password"
          className="inline-block mt-6 text-[13px] text-sage hover:text-sage-dark"
        >
          Request a reset link
        </Link>
      </AdminAuthShell>
    );

  return (
    <AdminAuthShell
      heading="Choose a new password"
      footer="Access is restricted to Human Home Living staff."
    >
      <form onSubmit={submit} data-testid="admin-reset-form">
        <p className="text-[14px] text-[#6E6A63] leading-relaxed mb-5">
          At least 12 characters. A phrase you will remember beats a short, complicated one.
        </p>
        <AdminAuthField
          label="New password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-reset-password"
        />
        <AdminAuthField
          label="Repeat password"
          type="password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="admin-reset-confirm"
        />
        {error && (
          <p data-testid="admin-reset-error" className="text-[13.5px] text-[#C0392B] mb-5">
            {error}
          </p>
        )}
        <AdminAuthButton busy={busy} data-testid="admin-reset-submit">
          {busy ? "Saving…" : "Save password"}
        </AdminAuthButton>
      </form>
    </AdminAuthShell>
  );
}
